/* The one Server-Sent Events reader. FRONTEND_RULE_BOOK.md §3: SSE goes through
   here, every other request goes through `client.ts`.

   Why `fetch` and not `EventSource`: an EventSource cannot send a header, and
   the API wants a bearer token. Why not axios: in a browser it has no streamed
   response body. So this reads the body itself, with the client's base URL for the path and
   auth header (asked for on every connection, so a renewed token is picked up),
   and does the three things EventSource would have done for us:

   - parses `id:` / `data:` / `:` frames, however the network chunks them;
   - remembers the last `id:` and reconnects from it (`?since=`) after 1 s when
     the connection drops, so a blip costs no event and repeats none;
   - stops when the caller says the event it just got was the last one, when
     the caller says a stream the server closed was meant to end there, or when
     `signal` aborts.

   A 4xx is an answer, not an outage, so it is thrown as an ApiError and not
   retried; a 5xx or a dead connection is retried until the caller aborts. */
import { authHeaders, baseUrlFor } from "./client";
import { ApiError } from "./errors";

export const RECONNECT_MS = 1_000;

export interface StreamOptions<E> {
  /** Resume after this event id; empty reads from the start. */
  since?: string;
  signal: AbortSignal;
  /** Return true when this was the last event wanted — the reader stops there. */
  onEvent: (event: E) => boolean | void;
  /** The server ended the body. Return true when it ended where it was meant to, and
      the reader stops; otherwise it is a dropped connection, and it reconnects. */
  onClose?: () => boolean;
}

export async function readStream<E>(url: string, { since = "", signal, onEvent, onClose }: StreamOptions<E>): Promise<void> {
  let cursor = since;
  const onFrame = (id: string, data: string): boolean => {
    if (id) cursor = id;
    try {
      return onEvent(JSON.parse(data) as E) === true;
    } catch (err) {
      console.warn("stream: skipped a frame that is not JSON", err);
      return false;
    }
  };
  while (!signal.aborted) {
    try {
      const query = cursor ? `${url.includes("?") ? "&" : "?"}since=${encodeURIComponent(cursor)}` : "";
      const res = await fetch(`${baseUrlFor(url)}${url}${query}`, {
        headers: { Accept: "text/event-stream", ...authHeaders() },
        signal,
      });
      if (!res.ok || !res.body) throw await failure(res);
      if (await readFrames(res.body, onFrame)) return;
      if (onClose?.()) return;
    } catch (err) {
      if (signal.aborted) return;
      if (err instanceof ApiError && err.status < 500) throw err;
      console.warn("stream: connection dropped, reconnecting", err);
    }
    await pause(RECONNECT_MS, signal);
  }
}

/** Frames out of a byte stream. True when `onFrame` asked to stop, false when the body ended. */
async function readFrames(
  body: ReadableStream<Uint8Array>,
  onFrame: (id: string, data: string) => boolean,
): Promise<boolean> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  let id = "";
  let data: string[] = [];
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return false;
      buffered += decoder.decode(value, { stream: true });
      const lines = buffered.split(/\r?\n/);
      buffered = lines.pop() ?? ""; // the last piece may be half a line
      for (const line of lines) {
        if (line === "") {
          if (data.length && onFrame(id, data.join("\n"))) return true;
          data = [];
          continue;
        }
        if (line.startsWith(":")) continue; // a comment: the heartbeat
        const colon = line.indexOf(":");
        const field = colon < 0 ? line : line.slice(0, colon);
        const value = colon < 0 ? "" : line.slice(colon + 1).replace(/^ /, "");
        if (field === "id") id = value;
        if (field === "data") data.push(value);
      }
    }
  } finally {
    reader.cancel().catch(() => undefined); // closes the connection; a body already done has nothing to cancel
  }
}

/** The error envelope as an ApiError, or the bare status when there is no envelope. */
async function failure(res: Response): Promise<ApiError> {
  const body = await res.json().catch(() => null); // a proxy's HTML page → no envelope
  return new ApiError(body?.message || `Stream failed (${res.status})`, res.status, body?.code ?? null, body?.request_id ?? null);
}

const pause = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal.aborted) return resolve();
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => (clearTimeout(timer), resolve()), { once: true });
  });
